import classNames from "classnames";
import { ContactAvatar } from "components/Atoms/Avatar";
import { PhosphorEnvelopeSimple, PhosphorPhone } from "components/Atoms/PhosphorIcon";
import { PhosphorLinkBreakButton } from "components/Atoms/PhosphorIconButton";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import CultureFlag from "components/Culture/CultureFlag";
import { ContactSidePanel } from "components/SidePanel/contact/ContactSidePanel";
import React from "react";
import UnlinkModal from "./UnlinkModal";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  company: server.dto.Company
  contactWithWorkRelation: server.dto.ContactWithWorkRelation
}

const ContactWithWorkRelationRowCard = ({ contactWithWorkRelation, company }: Props) => {
  const contact = contactWithWorkRelation.contact;
  const isSaving = React.useRef(false);
  const [isDefaultAttendee, setIsDefaultAttendee] = React.useState(contactWithWorkRelation.defaultSelect);
  const [unlinkModalOpen, setUnlinkModalOpen] = React.useState(false);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (contactWithWorkRelation.defaultSelect !== isDefaultAttendee) { setIsDefaultAttendee(contactWithWorkRelation.defaultSelect); }
  }, [contactWithWorkRelation, contactWithWorkRelation.defaultSelect]);


  const canEdit = company.permissions.canModify;
  const showDefaultAttendees = company.isArtist !== "no" && canEdit && contactWithWorkRelation.isActive;

  const onDefaultAttendeeChanged = async () => {
    if (isSaving.current) return;

    isSaving.current = true;
    setIsDefaultAttendee(!isDefaultAttendee);

    const instruction: server.dto.SetCompanyToContactLinkInstruction = {
      companyId: company.companyId,
      contactId: contact.contactId,
      isActive: contactWithWorkRelation.isActive,
      defaultSelect: !isDefaultAttendee
    };

    await window.CoreApi.CompanyToContact.SetLink(instruction);
    isSaving.current = false;
  };

  const onUnlinkModalClosed = (anythingChanged: boolean) => {
    setUnlinkModalOpen(false);
    if (anythingChanged) {
      queryClient.invalidateQueries({ queryKey: ["linked-contacts-to-company"] });
    }
  };

  const transparentIfInactive = (cssClasses?: string): string => {
    return classNames(cssClasses, {
      s1_transparent: !contactWithWorkRelation.isActive
    });
  };

  return <div className="linked-contact-row-card">
    <div>
      <ContactAvatar
        gender={contact.gender}
        profileImageUrl={contact.profileImageUrl}
        name={contact.name.fullName}
      />
    </div>
    <div className={transparentIfInactive("grow1 flex gap linked-contact-row-name-details")}>
      <div className="flex-column gap-mini" style={{ flex: "0 0 40%" }}>
        <div>
          <ContactSidePanel
            contactId={contact.contactId}
            trigger={<a className="table-main-link">
              {contact.name.fullName}
            </a>
            }
          />
        </div>
        <div className="s1_gray">
          {contact.position}
        </div>
      </div>

      <div className="flex-column gap-small" style={{ flex: "1 1 auto" }}>
        {(contact.primaryEmail) && <div className="flex gap" >
          <div><PhosphorEnvelopeSimple iconColor="gray" /></div>
          <div>
            <a href={`mailto:${contact.primaryEmail}`}>
              {contact.primaryEmail}
            </a>
          </div>
        </div>}

        {(contact.primaryMobile) && <div className="flex gap">
          <div><PhosphorPhone iconColor="gray" /></div>
          <div>
            {contact.primaryMobile}
          </div>
        </div>}
      </div>
    </div >
    <div>
      {showDefaultAttendees && <Checkbox
        checked={isDefaultAttendee}
        onChange={onDefaultAttendeeChanged}
        label={RESX.Contact.DefaultAttendee}
      />}
    </div>

    <div className={transparentIfInactive()}>
      <CultureFlag lcid={contact.culture} />
    </div>

    {
      canEdit && <PhosphorLinkBreakButton
        layout="in-square"
        onClick={() => setUnlinkModalOpen(true)} />
    }

    {
      unlinkModalOpen && <UnlinkModal
        contactWithWorkRelation={contactWithWorkRelation}
        company={company}
        onClose={onUnlinkModalClosed}
      />
    }

  </div >;
};

export default ContactWithWorkRelationRowCard;